import { z } from 'zod';
import { LIMITES } from './limites';

/**
 * Um anexo já enviado ao armazenamento, esperando para virar arquivo na
 * máquina.
 *
 * A `chave` é decidida pelo servidor e conferida na hora do envio — o painel
 * não escolhe onde o objeto mora. O `nome` viaja só para a mensagem poder
 * dizer "a foto contrato.png"; o arquivo no disco é nomeado pela chave, que
 * não passa por mão de ninguém.
 */
export const AnexoDaMensagemSchema = z.object({
  chave: z.string().min(1).max(300),
  mime: z.string().min(1).max(120),
  nome: z.string().min(1).max(200),
});
export type AnexoDaMensagem = z.infer<typeof AnexoDaMensagemSchema>;

export const SessaoEnviarSchema = z.object({
  sessaoId: z.string().min(1),
  texto: z.string().min(1).max(LIMITES.textoMensagem),
  anexos: z.array(AnexoDaMensagemSchema).max(LIMITES.anexosPorMensagem).default([]),
});
export type SessaoEnviar = z.infer<typeof SessaoEnviarSchema>;

/**
 * Nome do evento que a API manda à sala `usuario:<id>` de quem enviou quando
 * uma entrega em SEGUNDO PLANO termina (`ENTREGA_EM_SEGUNDO_PLANO_BYTES`).
 */
export const EVENTO_SESSAO_ENTREGA = 'sessao.entrega' as const;

/**
 * A resposta do `POST /sessoes/:id/enviar` quando a entrega foi para o segundo
 * plano: a mensagem foi ACEITA pela API (escopo, sessão, anexos conferidos), e
 * a máquina ainda está baixando os anexos. O fim chega por `sessao.entrega`
 * com o MESMO `entregaId`. Sem `entrega` na resposta, o envio já terminou —
 * o caminho síncrono de sempre.
 */
export const EntregaEmAndamentoSchema = z.strictObject({
  entregaId: z.string().min(1).max(64),
  /** Soma dos anexos, para a tela dizer quanto a máquina está baixando. */
  bytes: z.number().int().nonnegative(),
});
export type EntregaEmAndamento = z.infer<typeof EntregaEmAndamentoSchema>;

/**
 * O fim de uma entrega em segundo plano. `ok: false` traz o MESMO par
 * `{codigo, mensagem}` que a rota devolveria se tivesse esperado — a tela
 * traduz pelo código, como qualquer `ErroApi`. Ausente o código, a tela usa a
 * frase genérica do envio.
 */
export const SessaoEntregaSchema = z.discriminatedUnion('ok', [
  z.strictObject({
    sessaoId: z.string().min(1),
    entregaId: z.string().min(1).max(64),
    ok: z.literal(true),
  }),
  z.strictObject({
    sessaoId: z.string().min(1),
    entregaId: z.string().min(1).max(64),
    ok: z.literal(false),
    codigo: z.string().min(1).max(80).optional(),
    mensagem: z.string().max(500).optional(),
  }),
]);
export type SessaoEntrega = z.infer<typeof SessaoEntregaSchema>;

export const ModoPermissaoSchema = z.enum([
  'default',
  'acceptEdits',
  'plan',
  'auto',
  'dontAsk',
  'bypassPermissions',
]);
export type ModoPermissao = z.infer<typeof ModoPermissaoSchema>;

/**
 * As quatro respostas possíveis a um pedido de interação.
 *
 * Regra dura de segurança: o painel NUNCA envia `updatedInput` livre — quem
 * monta a entrada final é o daemon, a partir da entrada original que ele tem
 * em mãos. Daqui saem só respostas estruturadas: a escolha da pergunta
 * (`respostas`, chaveada pelo texto da pergunta; texto livre entra como valor
 * comum) e a saída do plano. Reescrever o comando que vai rodar não é um
 * poder que uma decisão de celular deve ter.
 *
 * As variantes são exportadas soltas porque o `PermissaoDecididaSchema` do
 * lado do daemon é a MESMA união com `requestId` — um contrato, duas pernas.
 */
export const DecisaoPermitirSchema = z.object({
  decisao: z.literal('permitir'),
  lembrarNaSessao: z.boolean().default(false),
});
export const DecisaoResponderSchema = z.object({
  decisao: z.literal('responder'),
  /** Pergunta → resposta. Multiseleção vira labels unidos por vírgula. */
  respostas: z.record(z.string().max(2_000), z.string().max(4_000)),
  anotacoes: z
    .record(z.string().max(2_000), z.object({ notas: z.string().max(2_000).optional() }))
    .optional(),
});
export const DecisaoPlanoSchema = z.object({
  decisao: z.literal('plano'),
  acao: z.enum(['aceitar_edicoes', 'perguntar_sempre', 'continuar_planejando']),
  /** O que falta no plano; vira a mensagem do deny em `continuar_planejando`. */
  observacao: z.string().max(2_000).optional(),
});
export const DecisaoNegarSchema = z.object({
  decisao: z.literal('negar'),
  mensagem: z.string().max(500).default('Negado pelo painel'),
  interromper: z.boolean().default(false),
});

export const DecisaoPermissaoSchema = z.discriminatedUnion('decisao', [
  DecisaoPermitirSchema,
  DecisaoResponderSchema,
  DecisaoPlanoSchema,
  DecisaoNegarSchema,
]);
export type DecisaoPermissao = z.infer<typeof DecisaoPermissaoSchema>;

export const ResponderPermissaoSchema = z.object({
  pedidoId: z.string().min(1),
  resposta: DecisaoPermissaoSchema,
});
export type ResponderPermissao = z.infer<typeof ResponderPermissaoSchema>;

export const TrechoEnviadoSchema = z.object({
  gravacaoId: z.string().min(1),
  ordem: z.number().int().nonnegative(),
  inicioMs: z.number().int().nonnegative(),
  fimMs: z.number().int().nonnegative(),
});
export type TrechoEnviado = z.infer<typeof TrechoEnviadoSchema>;
